import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Badge,
  IconButton,
  Drawer,
  Alert,
  Tooltip,
  CircularProgress,
  Chip,
  Tabs,
  Tab
} from '@mui/material';
import {
  WarningAmberRounded as WarningIcon,
  CloseRounded as CloseIcon,
  UpdateRounded as UpdateIcon,
  LocalShippingRounded as DirectIcon,
  AccountTreeRounded as TransitIcon,
  CheckCircleOutlineRounded as SuccessIcon,
  ArrowForwardRounded as ArrowIcon,
  AccessTimeRounded as TimeIcon
} from '@mui/icons-material';

const API_BASE = 'https://tbl.zerdegroup.kz/api/v1';

interface WarningDoc {
  id: string;
  documentNumber: string;
  status: number;
  statusName: string;
  type: number;
  typeMovement: number;
  fromPharmacy: string;
  toPharmacy: string;
  currentPoint?: string | null;
  updateDate: string;
  createdAt: string;
}

const AdminWarningsPanel: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [directWarnings, setDirectWarnings] = useState<WarningDoc[]>([]);
  const [transitWarnings, setTransitWarnings] = useState<WarningDoc[]>([]);
  const [extendingId, setExtendingId] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const fetchWarnings = async () => {
    try {
      const response = await axios.get(`${API_BASE}/admin/documents/warnings`);
      if (response.data.success) {
        setDirectWarnings(response.data.directWarnings || []);
        setTransitWarnings(response.data.transitWarnings || []);
      }
    } catch (error) {
      console.error('Ошибка при загрузке предупреждений:', error);
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => {
    fetchWarnings();
    const interval = setInterval(fetchWarnings, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Auto-switch tabs if one of the lists is loaded and the other is empty
  useEffect(() => {
    if (loaded) {
      if (directWarnings.length === 0 && transitWarnings.length > 0) {
        setActiveTab(1);
      } else if (directWarnings.length > 0 && transitWarnings.length === 0) {
        setActiveTab(0);
      }
    }
  }, [loaded, directWarnings.length, transitWarnings.length]);

  const handleExtend = async (id: string) => {
    setExtendingId(id);
    try {
      const response = await axios.put(`${API_BASE}/admin/documents/${id}/extend`);
      if (response.data.success) {
        setDirectWarnings(prev => prev.filter(w => w.id !== id));
        setTransitWarnings(prev => prev.filter(w => w.id !== id));
      }
    } catch (error) {
      console.error('Ошибка при продлении документа:', error);
    } finally {
      setExtendingId(null);
    }
  };

  const totalCount = directWarnings.length + transitWarnings.length;

  if (!loaded || totalCount === 0) return null;

  const getDaysStuck = (updateDate: string) => {
    const diffTime = Date.now() - new Date(updateDate).getTime();
    return Math.max(0, Math.floor(diffTime / (1000 * 60 * 60 * 24)));
  };

  const getPluralDays = (n: number) => {
    const lastDigit = n % 10;
    const lastTwoDigits = n % 100;
    if (lastTwoDigits >= 11 && lastTwoDigits <= 19) return `${n} дней`;
    if (lastDigit === 1) return `${n} день`;
    if (lastDigit >= 2 && lastDigit <= 4) return `${n} дня`;
    return `${n} дней`;
  };

  // Компонент одной строки документа (карточки)
  const DocListItem = ({ doc }: { doc: WarningDoc }) => {
    const daysStuck = getDaysStuck(doc.updateDate);
    const isTransit = doc.typeMovement === 2;
    const accentColor = isTransit ? '#d32f2f' : '#ed6c02';

    return (
      <Box
        key={doc.id}
        sx={{
          mx: { xs: 2, sm: 3 },
          my: 2,
          p: 2.5,
          bgcolor: '#ffffff',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderLeft: `5px solid ${accentColor}`,
          boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
            transform: 'translateY(-2px)',
            borderColor: 'divider',
            borderLeft: `5px solid ${accentColor}`
          }
        }}
      >
        {/* Header line: Doc number & Status */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5, gap: 1 }}>
          <Box>
            <Typography variant="subtitle2" fontWeight="800" sx={{ color: 'text.primary', fontSize: '0.95rem' }}>
              № {doc.documentNumber}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
              Создан: {new Date(doc.createdAt).toLocaleDateString('ru-RU')}
            </Typography>
          </Box>
          <Chip
            label={doc.statusName}
            size="small"
            sx={{
              bgcolor: 'action.hover',
              color: 'text.secondary',
              fontWeight: 600,
              fontSize: '0.7rem',
              height: 22
            }}
          />
        </Box>

        {/* Route visualization */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            bgcolor: 'grey.50',
            p: 1.25,
            borderRadius: 1.5,
            mb: 1.5,
            gap: 1
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', textTransform: 'uppercase', fontSize: '0.65rem', fontWeight: 700 }}>
              Откуда
            </Typography>
            <Typography variant="body2" fontWeight="600" noWrap sx={{ color: 'text.primary' }}>
              {doc.fromPharmacy || '-'}
            </Typography>
          </Box>
          <ArrowIcon sx={{ color: 'text.disabled', fontSize: 16 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', textTransform: 'uppercase', fontSize: '0.65rem', fontWeight: 700 }}>
              Куда
            </Typography>
            <Typography variant="body2" fontWeight="600" noWrap sx={{ color: 'text.primary' }}>
              {doc.toPharmacy || '-'}
            </Typography>
          </Box>
        </Box>

        {/* Current Location (Transit only, if available) */}
        {doc.currentPoint && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              bgcolor: 'info.50',
              p: 1.25,
              borderRadius: 1.5,
              mb: 1.5,
              borderLeft: '3px solid',
              borderColor: 'info.main'
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="info.main" sx={{ display: 'block', textTransform: 'uppercase', fontSize: '0.65rem', fontWeight: 700 }}>
                Текущая точка (РЦ / Транзит)
              </Typography>
              <Typography variant="body2" fontWeight="600" sx={{ color: 'text.primary', fontSize: '0.8rem' }}>
                {doc.currentPoint}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Footer info: Days stuck & action */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pt: 1.5,
            borderTop: '1px dashed',
            borderColor: 'divider',
            flexWrap: 'wrap',
            gap: 1
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: accentColor }}>
            <TimeIcon sx={{ fontSize: 16 }} />
            <Typography variant="caption" fontWeight="700">
              Активность: {getPluralDays(daysStuck)} назад
            </Typography>
          </Box>

          <Tooltip title="Сбросить таймер автоотмены и продлить лимит времени">
            <span>
              <IconButton
                color="primary"
                onClick={() => handleExtend(doc.id)}
                disabled={extendingId === doc.id}
                size="small"
                sx={{
                  bgcolor: 'primary.50',
                  '&:hover': { bgcolor: 'primary.100' },
                  borderRadius: 1.5,
                  px: 1.5,
                  py: 0.5,
                  display: 'flex',
                  gap: 0.5
                }}
              >
                {extendingId === doc.id ? (
                  <CircularProgress size={16} />
                ) : (
                  <>
                    <UpdateIcon sx={{ fontSize: 16 }} />
                    <Typography variant="button" sx={{ fontSize: '0.75rem', textTransform: 'none', fontWeight: 700 }}>
                      Продлить
                    </Typography>
                  </>
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
    );
  };

  const EmptyState = ({ message }: { message: string }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, px: 3, textAlign: 'center' }}>
      <Box sx={{
        width: 64,
        height: 64,
        borderRadius: '50%',
        bgcolor: 'success.50',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'success.main',
        mb: 2
      }}>
        <SuccessIcon sx={{ fontSize: 32 }} />
      </Box>
      <Typography variant="subtitle1" fontWeight="700" color="text.primary" gutterBottom>
        Все в порядке
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {message}
      </Typography>
    </Box>
  );

  return (
    <>
      {/* Кнопка для тулбара */}
      <Tooltip title="Проблемные документы на контроле">
        <IconButton
          onClick={() => setOpen(true)}
          size="small"
          color="error"
          sx={{
            border: '1px solid',
            borderColor: 'error.main',
            borderRadius: 1,
            bgcolor: 'error.50'
          }}
        >
          <Badge badgeContent={totalCount} color="warning" max={99}>
            <WarningIcon fontSize="small" />
          </Badge>
        </IconButton>
      </Tooltip>

      {/* Drawer */}
      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: { width: { xs: '100%', sm: 460 }, bgcolor: '#fafafa', display: 'flex', flexDirection: 'column' }
        }}
      >
        {/* Заголовок */}
        <Box sx={{ px: 3, py: 2.5, bgcolor: '#ffffff', borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Badge badgeContent={totalCount} color="error" max={999}>
              <WarningIcon color="warning" />
            </Badge>
            <Typography variant="h6" fontWeight="800" color="text.primary">
              Требуют контроля
            </Typography>
          </Box>
          <IconButton onClick={() => setOpen(false)} size="small" edge="end">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Описание */}
        <Box>
          <Alert severity="warning" sx={{ borderRadius: 0, py: 1 }}>
            <Typography variant="caption" sx={{ display: 'block', fontWeight: 500, lineHeight: 1.35 }}>
              Документы зависли в промежуточных статусах. Продлите срок, чтобы сбросить таймер автоматической отмены и скрытия, или свяжитесь с исполнителями.
            </Typography>
          </Alert>
        </Box>

        {/* Вкладки (Tabs) */}
        <Box sx={{ position: 'sticky', top: 0, zIndex: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(e, val) => setActiveTab(val)}
            variant="fullWidth"
            sx={{
              bgcolor: '#ffffff',
              borderBottom: '1px solid',
              borderColor: 'divider',
              '& .MuiTabs-indicator': {
                backgroundColor: activeTab === 0 ? '#ed6c02' : '#d32f2f',
                height: 3,
              }
            }}
          >
            <Tab
              sx={{ textTransform: 'none', py: 1.5 }}
              label={
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DirectIcon sx={{ color: activeTab === 0 ? '#ed6c02' : 'text.secondary', fontSize: 18 }} />
                    <Typography variant="subtitle2" fontWeight="700" sx={{ color: activeTab === 0 ? '#ed6c02' : 'text.secondary', fontSize: '0.85rem' }}>
                      Прямые
                    </Typography>
                    <Chip
                      label={directWarnings.length}
                      size="small"
                      sx={{
                        bgcolor: activeTab === 0 ? '#ed6c02' : 'action.selected',
                        color: activeTab === 0 ? '#fff' : 'text.secondary',
                        fontWeight: 700,
                        height: 18,
                        fontSize: '0.65rem',
                        '& .MuiChip-label': { px: 0.8 }
                      }}
                    />
                  </Box>
                  <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.65rem' }}>
                    лимит 7 дней
                  </Typography>
                </Box>
              }
            />
            <Tab
              sx={{ textTransform: 'none', py: 1.5 }}
              label={
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TransitIcon sx={{ color: activeTab === 1 ? '#d32f2f' : 'text.secondary', fontSize: 18 }} />
                    <Typography variant="subtitle2" fontWeight="700" sx={{ color: activeTab === 1 ? '#d32f2f' : 'text.secondary', fontSize: '0.85rem' }}>
                      Через РЦ
                    </Typography>
                    <Chip
                      label={transitWarnings.length}
                      size="small"
                      sx={{
                        bgcolor: activeTab === 1 ? '#d32f2f' : 'action.selected',
                        color: activeTab === 1 ? '#fff' : 'text.secondary',
                        fontWeight: 700,
                        height: 18,
                        fontSize: '0.65rem',
                        '& .MuiChip-label': { px: 0.8 }
                      }}
                    />
                  </Box>
                  <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.65rem' }}>
                    лимит 15 дней
                  </Typography>
                </Box>
              }
            />
          </Tabs>
        </Box>

        {/* Список */}
        <Box sx={{ overflowY: 'auto', flexGrow: 1, pb: 4 }}>
          {activeTab === 0 ? (
            directWarnings.length > 0 ? (
              <Box sx={{ mt: 1 }}>
                {directWarnings.map(doc => <DocListItem key={doc.id} doc={doc} />)}
              </Box>
            ) : (
              <EmptyState message="Нет зависших документов по прямым перемещениям." />
            )
          ) : (
            transitWarnings.length > 0 ? (
              <Box sx={{ mt: 1 }}>
                {transitWarnings.map(doc => <DocListItem key={doc.id} doc={doc} />)}
              </Box>
            ) : (
              <EmptyState message="Нет зависших документов через РЦ." />
            )
          )}
        </Box>
      </Drawer>
    </>
  );
};

export default AdminWarningsPanel;
